import { useState } from "react";
import { useAtomValue } from "jotai";
import { X } from "lucide-react";
import { localeAtom } from "../../stores/locale";
import { useT } from "../../lib/i18n";
import { feedbackApi, type Ticket, type TicketKind } from "../../api/client";
import CosmicButton from "../../components/CosmicButton";
import ObserveTagline from "./ObserveTagline";

// Local UI state only (open/close, draft fields) → useState, not an atom
// (manifest §1). accountLogin is filing context — which galaxy the user was
// viewing — never an access key. `context` is the structured view context
// (page / galaxy / member / domain) attached when opened from a specific
// screen; it is shown to the user before sending (no hidden collection) and
// stored verbatim in the ticket's context JSON column.
export default function NewTicketModal({
  accountLogin,
  context,
  defaultSubject,
  onClose,
  onCreated,
}: {
  accountLogin?: string;
  context?: Record<string, unknown>;
  defaultSubject?: string;
  onClose: () => void;
  onCreated: (ticket: Ticket) => void;
}) {
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);
  const [kind, setKind] = useState<TicketKind>("feedback");
  const [subject, setSubject] = useState(() => defaultSubject ?? "");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const canSubmit = subject.trim() !== "" && body.trim() !== "" && !sending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSending(true);
    setError(false);
    try {
      const ticket = await feedbackApi.create({
        kind,
        subject: subject.trim(),
        body: body.trim(),
        account_login: accountLogin,
        context,
      });
      onCreated(ticket);
    } catch (e) {
      console.error("create ticket failed", e);
      setError(true);
      setSending(false);
    }
  };

  const kinds: TicketKind[] = ["feedback", "bug", "question"];

  // Shown to the user before sending — context is attached transparently,
  // never silently collected.
  const contextEntries = Object.entries(context ?? {}).filter(
    ([, v]) => v != null && v !== "",
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-lg"
      style={{ background: "rgba(6, 9, 18, 0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[32rem] rounded-xl p-2xl"
        style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-lg flex items-start justify-between">
          <div>
            <h2 className="text-base font-medium" style={{ color: "var(--text-strong)" }}>
              {t("feedback.new")}
            </h2>
            <ObserveTagline className="mt-sm" />
          </div>
          <button
            onClick={onClose}
            style={{ color: "var(--text-dim)" }}
            aria-label={t("feedback.cancel")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-md">
          <div className="flex gap-md">
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className="rounded-lg px-md py-sm text-xs transition-colors"
                style={{
                  border: "1px solid",
                  borderColor: kind === k ? "var(--gold)" : "var(--border)",
                  color: kind === k ? "var(--gold)" : "var(--text-muted)",
                  fontFamily: "var(--mono)",
                }}
              >
                {t(`feedback.kind.${k}`)}
              </button>
            ))}
          </div>

          <label className="flex flex-col gap-sm">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {t("feedback.subject")}
            </span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("feedback.subjectPlaceholder")}
              maxLength={255}
              className="rounded-lg px-md py-md text-sm outline-none"
              style={{ background: "var(--bg-void)", border: "1px solid var(--border)", color: "var(--text)" }}
            />
          </label>

          <label className="flex flex-col gap-sm">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {t("feedback.message")}
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("feedback.messagePlaceholder")}
              rows={5}
              className="resize-y rounded-lg px-md py-md text-sm outline-none"
              style={{ background: "var(--bg-void)", border: "1px solid var(--border)", color: "var(--text)" }}
            />
          </label>

          {contextEntries.length > 0 && (
            <div
              className="rounded-lg px-md py-md"
              style={{ background: "var(--bg-void)", border: "1px solid var(--border)" }}
            >
              <p
                className="mb-sm text-[10px] uppercase"
                style={{ fontFamily: "var(--mono)", letterSpacing: "0.1em", color: "var(--text-dim)" }}
              >
                {t("feedback.attachedContext")}
              </p>
              <div className="flex flex-wrap gap-sm">
                {contextEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="rounded px-sm py-xs text-[11px]"
                    style={{
                      fontFamily: "var(--mono)",
                      color: "var(--text-muted)",
                      backgroundColor: "rgba(122,133,153,0.12)",
                    }}
                  >
                    {k}: {String(v)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs" style={{ color: "var(--gb-red)" }}>
              {t("feedback.loadError")}
            </p>
          )}

          <div className="mt-sm flex justify-end gap-md">
            <CosmicButton variant="ghost" size="sm" onClick={onClose} disabled={sending}>
              {t("feedback.cancel")}
            </CosmicButton>
            <CosmicButton variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
              {sending ? t("feedback.sending") : t("feedback.send")}
            </CosmicButton>
          </div>
        </div>
      </div>
    </div>
  );
}
